import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, CheckCircle, Edit3, Calendar, ChevronDown } from 'lucide-react';
import { addDays, addYears, format, eachMonthOfInterval, startOfMonth, endOfMonth, eachDayOfInterval, isBefore, isAfter, startOfDay } from 'date-fns';

const scriptOptions = [
  { value: 'full-script', title: 'Hire What a Story for the Script', description: 'Let our experts create a compelling script from scratch', icon: FileText },
  { value: 'no-script', title: 'We already have the script', description: 'You have a ready-to-use script for production', icon: CheckCircle },
  { value: 'improve-script', title: 'We have a draft and need an expert for improvements', description: 'Polish your existing script with professional input', icon: Edit3 }
];

const PremiumDatePicker = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(null); // 'day', 'month', 'year' or null
  const containerRef = useRef(null);
  const minDate = startOfDay(addDays(new Date(), 7));
  const maxDate = addYears(new Date(), 1);
  const selectedDate = value ? new Date(value) : minDate;

  // Generate valid options
  const years = [minDate.getFullYear(), maxDate.getFullYear()].filter((v, i, a) => a.indexOf(v) === i);
  const months = eachMonthOfInterval({ start: minDate, end: maxDate });

  const getDaysInMonth = () => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    return eachDayOfInterval({ start, end });
  };

  const handleSelect = (type, val) => {
    let newDate = new Date(selectedDate);
    if (type === 'year') newDate.setFullYear(val);
    if (type === 'month') newDate.setMonth(val);
    if (type === 'day') newDate.setDate(val);

    // Clamp to range
    if (isBefore(newDate, minDate)) newDate = minDate;
    if (isAfter(newDate, maxDate)) newDate = maxDate;

    onChange({ target: { value: format(newDate, 'yyyy-MM-dd') } });
    setIsOpen(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const Segment = ({ type, label, displayValue, options, currentVal }) => (
    <div className="relative flex-1">
      <button
        type="button"
        onClick={() => setIsOpen(isOpen === type ? null : type)}
        className={`w-full flex items-center justify-between p-4 bg-white/5 border rounded-xl transition-all duration-300 ${
          isOpen === type ? 'border-amber-500 ring-2 ring-amber-500/20' : 'border-white/10 hover:border-white/20'
        }`}
      >
        <div className="text-left">
          <span className="block text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</span>
          <span className="text-white font-medium">{displayValue}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isOpen === type ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen === type && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 5, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute z-50 top-full left-0 w-full mt-2 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl max-h-60 overflow-y-auto custom-scrollbar"
          >
            <div className="p-2 grid grid-cols-1 gap-1">
              {options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(type, opt.value)}
                  className={`px-4 py-3 text-left rounded-lg transition-colors ${
                    currentVal === opt.value ? 'bg-amber-500 text-black font-bold' : 'text-gray-300 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div ref={containerRef} className="flex gap-3">
      <Segment
        type="day"
        label="Day"
        displayValue={format(selectedDate, 'do')}
        currentVal={selectedDate.getDate()}
        options={getDaysInMonth().map(d => ({ value: d.getDate(), label: format(d, 'do') }))}
      />
      <Segment
        type="month"
        label="Month"
        displayValue={format(selectedDate, 'MMMM')}
        currentVal={selectedDate.getMonth()}
        options={Array.from({ length: 12 }, (_, i) => ({ value: i, label: format(new Date(2024, i, 1), 'MMMM') }))}
      />
      <Segment
        type="year"
        label="Year"
        displayValue={selectedDate.getFullYear()}
        currentVal={selectedDate.getFullYear()}
        options={years.map(y => ({ value: y, label: y }))}
      />
    </div>
  );
};

const StepThree = ({ wizardData, updateWizardData, onNext, onPrev }) => {
  // Initialize deadline if empty on mount
  useEffect(() => {
    if (!wizardData.deadline) {
      const defaultDate = format(addDays(new Date(), 7), 'yyyy-MM-dd');
      updateWizardData({ deadline: defaultDate });
    }
  }, []);

  const handleScriptChange = (value) => updateWizardData({ scriptNeeded: value });
  const handleAudienceChange = (e) => updateWizardData({ targetAudience: e.target.value });
  const handleDeadlineChange = (e) => updateWizardData({ deadline: e.target.value });

  const canProceed = !!wizardData.scriptNeeded;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-4">Strategy Details</h2>
        <p className="text-gray-400">Tell us more about your project requirements</p>
      </div>

      <div className="space-y-8">
        {/* Script Section */}
        <div>
          <h3 className="text-xl font-semibold text-white mb-4">Script Requirements</h3>
          <div className="space-y-4">
            {scriptOptions.map((option, index) => {
              const IconComponent = option.icon;
              const isSelected = wizardData.scriptNeeded === option.value;
              return (
                <motion.div
                  key={option.value}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ scale: 1.01 }}
                  onClick={() => handleScriptChange(option.value)}
                  className={`p-6 rounded-xl cursor-pointer transition-all duration-300 backdrop-blur-md ${
                    isSelected 
                      ? 'bg-amber-500/20 border-2 border-amber-500/50 shadow-lg shadow-amber-900/30' 
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg flex-shrink-0 ${isSelected ? 'bg-amber-500/30' : 'bg-white/10'}`}>
                      <IconComponent className={`w-6 h-6 ${isSelected ? 'text-amber-400' : 'text-white'}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-semibold text-lg mb-2 ${isSelected ? 'text-amber-400' : 'text-white'}`}>{option.title}</h4>
                      <p className="text-gray-400 text-sm">{option.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-amber-500' : 'bg-white/20 border border-white/30'
                    }`}>
                      {isSelected && <div className="w-2.5 h-2.5 bg-black rounded-full" />}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Audience Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <label className="block text-xl font-semibold text-white mb-4">Your Target Audience</label>
          <textarea
            value={wizardData.targetAudience}
            onChange={handleAudienceChange}
            placeholder="Describe your target audience..."
            rows={3}
            className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-amber-500/50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all"
          />
        </motion.div>

        {/* Deadline Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <div className="flex items-center space-x-2 mb-4 text-xl font-semibold text-white">
            <Calendar className="w-5 h-5 text-amber-500" />
            <label>Project Deadline</label>
          </div>
          <PremiumDatePicker value={wizardData.deadline} onChange={handleDeadlineChange} />
        </motion.div>
      </div>

      <div className="flex justify-between pt-4">
        <button
          onClick={onPrev}
          className="px-8 py-3 rounded-xl font-semibold bg-white/10 text-white hover:bg-white/20 transition-all"
        >
          ← Previous
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`px-10 py-3 rounded-xl font-bold transition-all ${
            canProceed
              ? 'bg-amber-500 text-black hover:bg-amber-400 shadow-xl shadow-amber-900/40'
              : 'bg-gray-800 text-gray-500 cursor-not-allowed'
          }`}
        >
          Final Step →
        </button>
      </div>
    </div>
  );
};

export default StepThree;